import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Search, CalendarIcon, Clock } from "lucide-react";
import { format, startOfDay, endOfDay, addDays, subDays } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const generateHourOptions = () => {
  return Array.from({ length: 24 }, (_, i) => ({
    value: i.toString(),
    label: `${i.toString().padStart(2, "0")}:00`,
  }));
};

const DateRangeFilter = ({ onFilterChange }) => {
  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [closingTime, setClosingTime] = useState("4"); // Por defecto 4:00

  const hourOptions = generateHourOptions();

  const getCurrentOperatingDay = () => {
    const now = new Date();
    const currentHour = now.getHours();

    // Si estamos después de medianoche pero antes de la hora de cierre,
    // consideramos que seguimos en el día operativo anterior
    if (currentHour < parseInt(closingTime)) {
      return subDays(now, 1);
    }
    return now;
  };

  const handleDateChange = (date, field) => {
    const newDate = date;
    setDateRange((prev) => ({
      ...prev,
      [field]: field === "startDate" ? startOfDay(newDate) : endOfDay(newDate),
    }));

    if (field === "startDate") {
      setStartOpen(false);
    } else {
      setEndOpen(false);
    }
  };

  const handleClosingTimeChange = (newTime) => {
    setClosingTime(newTime);
  };

  const applyFilter = () => {
    onFilterChange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      closingTime: parseInt(closingTime),
    });
  };

  const setToday = () => {
    const operatingDay = getCurrentOperatingDay();
    setDateRange({
      startDate: startOfDay(operatingDay),
      endDate: endOfDay(operatingDay),
    });
  };

  const setYesterday = () => {
    const operatingDay = getCurrentOperatingDay();
    const yesterday = subDays(operatingDay, 1);
    setDateRange({
      startDate: startOfDay(yesterday),
      endDate: endOfDay(yesterday),
    });
  };

  const setLastWeek = () => {
    const operatingDay = getCurrentOperatingDay();
    setDateRange({
      startDate: startOfDay(subDays(operatingDay, 7)),
      endDate: endOfDay(operatingDay),
    });
  };

  useEffect(() => {
    setToday();
  }, []);

  return (
    <Card className="mb-6 border-orange-200">
      <CardContent className="pt-6">
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Primera fila: Fechas */}
            <div className="flex flex-col md:flex-row gap-4 flex-1">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Desde
                </label>
                <Popover open={startOpen} onOpenChange={setStartOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal border-gray-200",
                        !dateRange.startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.startDate ? (
                        format(dateRange.startDate, "PPP", { locale: es })
                      ) : (
                        <span>Seleccione una fecha</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateRange.startDate}
                      onSelect={(date) => handleDateChange(date, "startDate")}
                      initialFocus
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hasta
                </label>
                <Popover open={endOpen} onOpenChange={setEndOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal border-gray-200",
                        !dateRange.endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.endDate ? (
                        format(dateRange.endDate, "PPP", { locale: es })
                      ) : (
                        <span>Seleccione una fecha</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateRange.endDate}
                      onSelect={(date) => handleDateChange(date, "endDate")}
                      initialFocus
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Selector de hora de corte */}
            {/* <div className="md:w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora de Corte
              </label>
              <Select
                value={closingTime}
                onValueChange={handleClosingTimeChange}
              >
                <SelectTrigger className="w-full">
                  <Clock className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Seleccione hora" />
                </SelectTrigger>
                <SelectContent>
                  {hourOptions.map((hour) => (
                    <SelectItem key={hour.value} value={hour.value}>
                      {hour.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div> */}
          </div>

          {/* Segunda fila: Botones */}
          <div className="flex flex-col sm:flex-row gap-2 md:gap-4">
            <Button
              onClick={setToday}
              variant="outline"
              className="flex-1 border-orange-200 hover:bg-orange-50"
            >
              Hoy
            </Button>
            <Button
              onClick={setYesterday}
              variant="outline"
              className="flex-1 border-orange-200 hover:bg-orange-50"
            >
              Ayer
            </Button>
            <Button
              onClick={setLastWeek}
              variant="outline"
              className="flex-1 border-orange-200 hover:bg-orange-50"
            >
              Última Semana
            </Button>
            <Button
              onClick={applyFilter}
              className="flex-1 bg-orange-500 text-white hover:bg-orange-600"
            >
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DateRangeFilter;
